// TASK-603 signal design: identical causes aggregate. 311 raw one-per-ISIN cards
// become a handful of actionable groups keyed on (category, cause). Pure —
// shared by the Alerts page, the Change Radar alerts tab and the bell popover.
import type { ActionPolicy, DashboardAlert, AlertLevel } from '../types/dashboard';
import { namesAClient } from './clientDisplayName';

/**
 * TASK-722: the scope an alert applies at. "Unassigned" used to cover the last two, which
 * implied a work-assignment feature that does not exist and invited the RM to go and assign
 * somebody. They are different facts and must stay different:
 *   client — it concerns this client
 *   book   — it genuinely applies across the book (a portfolio-level signal with no client)
 *   none   — it simply has no client (a task or meeting occurrence)
 * Measured on the live book 2026-07-29: all 287 client-less live alerts are `none`
 * (task_completed, task_high_priority_created, meeting_tasks_generated) — not one is a
 * book-wide portfolio signal, so collapsing them would have mislabelled every single one.
 */
export type AlertClientScope = 'client' | 'book' | 'none';

/**
 * Categories whose client-less alerts are genuinely BOOK-WIDE rather than merely unscoped.
 * A portfolio or compliance signal with no client applies across the book; a completed task
 * with no client is just a task that had no client.
 */
const BOOK_WIDE_CATEGORIES = new Set(['portfolio', 'compliance', 'transaction_monitoring',
                                      'media_monitoring']);

export interface AlertGroupClient {
  clientId?: string;
  clientName: string;
  /**
   * TASK-756: the translation key for `clientName` when there IS no client — 'book' and
   * 'none' are the two labels TASK-722 split apart, and they must stay separately
   * translatable or they re-collapse in the first language that has one word for both.
   * Null for a real client, whose name is data and must never be translated.
   */
  clientNameKey: string | null;
  scope: AlertClientScope;
  /** Human position names (security name preferred, ISIN fallback — never blank). */
  positions: string[];
  chf: number;
}

export interface AlertGroup {
  key: string;
  category: DashboardAlert['category'];
  causeKey: string;
  level: AlertLevel;
  causeLabel: string;
  /**
   * TASK-756: translation keys carried ALONGSIDE the English text rather than replacing it.
   * `groupAlerts` is a pure function shared by the Alerts page, the Radar and the bell, so
   * threading a translator through it would change three call sites and make the util depend
   * on React context. Components render `t(causeLabelKey, causeLabel)` — the English stays
   * the default value, so an unfilled locale degrades to a real sentence, never to a key.
   * Null when the cause has no curated copy and the fallback is the alert's own description.
   */
  causeLabelKey: string | null;
  sentenceKey: string | null;
  /** One plain-language sentence describing the cause. */
  sentence: string;
  alerts: DashboardAlert[];
  clients: AlertGroupClient[];
  /** Total CHF exposure across members; null when no member carries a value. */
  totalChf: number | null;
  memberIds: string[];
  /**
   * TASK-732: the server policy for this cause. Groups are keyed on (category, cause), so
   * one policy governs every member — taken from the first member, never composed here.
   */
  actionPolicy: ActionPolicy | null;
  /**
   * TASK-819: how the members' severities are actually distributed. `level` above is their
   * MAX, which is what made a two-member `kyc_staged_pending` group badge HIGH when one of
   * its members was medium — and made the severity tiles (server counts over every
   * unresolved row) disagree with the sum of the badges (max per rendered page-group).
   * A card must not claim a severity no member has.
   */
  levelCounts: Record<AlertLevel, number>;
}

// TASK-820: `CAUSE_COPY` is GONE.
//
// It was four hand-written entries against a server catalogue of TWELVE, so every source it
// did not know — `kyc_staged_pending`, `reprofile_due`, `client_portal_upload` — fell back to
// its raw enum key and sat on screen beside properly titled groups. That is not missing copy:
// the copy existed on the server and was not wired up, and every new source arrived here as an
// enum until somebody remembered to edit this file. Four already had.
//
// The label now comes from `alert.actionLabel`, projected from
// `shared/schemas/signal_source_labels`. A source the catalogue does not name renders as an
// honest unknown — see `causeLabelOf`.

const LEVEL_RANK: Record<AlertLevel, number> = { critical: 3, high: 2, medium: 1, info: 0 };

/**
 * Causes that must NEVER aggregate (TASK-672/678, deep review #3).
 *
 * A meeting reminder is per-SESSION and carries its own Start / Prepare / Reschedule /
 * Cancel deep-links. Two reminders collapsed into one card left the RM a card with NO action
 * at all, and client names that navigated to the DNA tab instead of the meeting. Aggregating
 * by cause is simply wrong for per-entity, per-action alerts: key them on the alert so each
 * stays its own actionable card.
 *
 * TASK-819 UPDATED THE MECHANICS THIS DESCRIBES — the original text is preserved above as the
 * REASON, and here is what changed, because this file already records that a stale comment is
 * why the missing-`deadline` bug went unnoticed:
 *   * `AlertsPage` no longer splits one-member groups out to `AlertList` → `AlertCard`.
 *     There is one list and one component, and `AlertList`/`AlertCard` have no live importer.
 *   * `AlertGroupCard` now mounts a real `AlertRow` per member, so it DOES render
 *     `quickActions`, snooze and deadline — the "card with no action" failure is gone.
 *   * the client name no longer navigates to the DNA tab for every cause (`DNA_TAB_CAUSES`).
 *
 * So the reason this hatch was opened has been addressed by other means. It is KEPT because
 * one card per reminder is still the right shape, and retiring it is a change to GROUPING —
 * not to rendering — which nothing has yet argued for.
 *
 * Deep review #5 corrected the component names above — the previous comment said the radar
 * renders `AlertCard`, which is why nobody checked that `AlertRow` never read `deadline`.
 */
const PER_ALERT_CAUSES = new Set([
  'meeting_upcoming',
  'meeting_starting_soon',
  'meeting_overdue',
]);

/**
 * TASK-820: the group's heading, from the server catalogue.
 *
 * A source the server has not named gets an HONEST UNKNOWN, never its enum key. The enum is
 * an identifier; showing it to an advisor tells them nothing and looks like a bug — which is
 * precisely what `kyc_staged_pending` looked like on the Alerts page.
 *
 * The translation key is only set when the server supplied a label: TASK-756's rule is that a
 * string nobody wrote must not be given an invented translation.
 */
export function causeLabelOf(alert: DashboardAlert): { text: string; key: string | null } {
  const label = alert.actionLabel?.trim();
  if (label) {
    return { text: label, key: `alerts.cause.${causeKeyOf(alert)}.label` };
  }
  return { text: 'Unnamed signal type', key: 'alerts.cause.unknown' };
}

/** Cause key: sourceEvent when present, else the title prefix before \':\'. */
export function causeKeyOf(alert: DashboardAlert): string {
  if (alert.sourceEvent) return alert.sourceEvent;
  const idx = alert.title.indexOf(':');
  return idx > 0 ? alert.title.slice(0, idx).trim() : alert.title.trim();
}

/** Human position label for one alert — security name > ISIN > entity id, never blank. */
export function positionLabelOf(alert: DashboardAlert): string | null {
  const meta = alert.metadata ?? {};
  const name = typeof meta.security_name === 'string' && meta.security_name ? meta.security_name : null;
  const isin = typeof meta.isin === 'string' && meta.isin ? meta.isin : null;
  if (alert.relatedEntityType === 'instrument' || name || isin) {
    return name ?? isin ?? alert.relatedEntityId ?? null;
  }
  return null;
}

function chfOf(alert: DashboardAlert): number | null {
  const v = alert.metadata?.market_value_chf;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export function groupAlerts(alerts: DashboardAlert[]): AlertGroup[] {
  const groups = new Map<string, AlertGroup>();

  for (const alert of alerts) {
    const causeKey = causeKeyOf(alert);
    const key = PER_ALERT_CAUSES.has(causeKey)
      ? `${alert.category}:${causeKey}:${alert.id}`
      : `${alert.category}:${causeKey}`;
    let group = groups.get(key);
    if (!group) {
      const label = causeLabelOf(alert);
      group = {
        key,
        category: alert.category,
        causeKey,
        level: alert.level,
        causeLabel: label.text,
        // Only CURATED copy gets a key. A cause that falls back to the alert's own title or
        // description is server data, not UI copy, and translating it would be inventing a
        // translation for a string nobody wrote.
        causeLabelKey: label.key,
        sentenceKey: null,
        sentence: alert.description ?? '',
        alerts: [],
        clients: [],
        totalChf: null,
        memberIds: [],
        actionPolicy: alert.actionPolicy ?? null,
        levelCounts: { critical: 0, high: 0, medium: 0, info: 0 },
      };
      groups.set(key, group);
    }

    group.alerts.push(alert);
    group.memberIds.push(alert.id);
    if (LEVEL_RANK[alert.level] > LEVEL_RANK[group.level]) group.level = alert.level;
    // TASK-819: the max still drives the card's colour; the distribution is what the badge
    // states, so filtering to one level cannot make a card's severity change meaning.
    group.levelCounts[alert.level] = (group.levelCounts[alert.level] ?? 0) + 1;

    const chf = chfOf(alert);
    if (chf !== null) group.totalChf = (group.totalChf ?? 0) + chf;

    // TASK-841: a UUID is not a name, and neither is this row's own client id. Without
    // this the Alerts page would render whatever the column held — and the check that is
    // supposed to guard it (`relatedClientName != null`) passes on exactly those values,
    // which is how one such row reported itself as "named".
    const named = namesAClient(alert.relatedClientName, alert.relatedClientId)
      ? alert.relatedClientName?.trim()
      : undefined;
    // TASK-817: scope follows the CLIENT ID first, the name second. An alert carrying a
    // client id is about that client even when its emitter forgot to send the name — the
    // old name-only derivation labelled four of five signal types "Book-wide" and then
    // navigated from that label to a real client's page.
    const scope: AlertClientScope = named || alert.relatedClientId
      ? 'client'
      : BOOK_WIDE_CATEGORIES.has(alert.category)
        ? 'book'
        : 'none';
    const clientName =
      named ??
      (scope === 'client'
        ? 'Client (name unavailable)'
        : scope === 'book'
          ? 'Book-wide'
          : 'No client scope');
    const clientNameKey = named ? null : `alerts.scope.${scope}`;
    // TASK-817: the label fallback applies ONLY to alerts with no client id. It used to run
    // unconditionally, so the first unnamed alert created `{clientId: <someone>, clientName:
    // 'Book-wide'}` and every LATER client's alert failed the id test, passed the label test,
    // and merged into that entry — three distinct clients rendered as one, with their CHF
    // summed and `clients[0].clientId` decided by load order. Two alerts with different ids
    // must never merge, whatever they are labelled. Alerts genuinely WITHOUT an id still
    // coalesce by label, which is TASK-722's behaviour and is deliberately preserved.
    let client = group.clients.find((c) =>
      alert.relatedClientId
        ? c.clientId === alert.relatedClientId
        : !c.clientId && c.clientName === clientName,
    );
    if (!client) {
      client = {
        clientId: alert.relatedClientId, clientName, clientNameKey, scope, positions: [], chf: 0,
      };
      group.clients.push(client);
    }
    // TASK-817 (review): the entry is created by the FIRST alert for this client, so a
    // group holding one alert with a name and one without renders whichever arrived first.
    // A real name always supersedes the placeholder — a mix is normal while pre-fix rows are
    // still live, and while `portfolio-service`'s best-effort lookup can return null for one
    // breach and the name for its sibling.
    if (named && client.clientNameKey !== null) {
      client.clientName = named;
      client.clientNameKey = null;
      client.scope = 'client';
    }

    const pos = positionLabelOf(alert);
    if (pos && !client.positions.includes(pos)) client.positions.push(pos);
    if (chf !== null) client.chf += chf;
  }

  return [...groups.values()].sort(
    (a, b) => LEVEL_RANK[b.level] - LEVEL_RANK[a.level] || b.alerts.length - a.alerts.length,
  );
}
